import React from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import {t} from 'i18next';

import {TruckBulky} from 'assets/icons/truckBulky';
import {VoucherIcon} from 'assets/icons/VoucherIcon';
import theme from 'config/theme';
import ReactNative from 'services/react-native';
import {SlabPriceTier} from 'types';
import {formatPrice, formatPriceWithCurrency, getCurrencySymbol} from 'utils/currency';
import {getSlabPriceValue, getPricePerUnit, getUnitPerProduct} from 'utils/product';

import {UnitPrice, FavListDiscountWeightedFreshKgText} from './styles';
import {
  PriceStyled,
  PriceBox,
  PriceStriked,
  StyledSlabPriceInfo,
  StyledDicountContainer,
  StyledUnitPerProductInfo,
  StyledDiscountBox
} from './styles';

export type DiscountPriceBoxProps = {
  priceUnit: string;
  originPrice: number;
  displayPrice: number;
  className?: string;
  slabPriceTiers?: SlabPriceTier[];
  productCount?: number;
  isShowPerUnitPrice?: boolean;
  productSize?: string;
  productId?: string;
  isWeightedFresh?: boolean;
  isInstallmentOrder?: boolean;
  showKg?: boolean;
  moq?: number;
  isVoucherUsed?: boolean;
};

export const DiscountPriceBox: React.FC<DiscountPriceBoxProps> = ({
  slabPriceTiers = [],
  productCount = 0,
  isShowPerUnitPrice,
  productSize = '',
  productId,
  isWeightedFresh,
  isInstallmentOrder = false,
  showKg,
  moq = 1,
  isVoucherUsed = false,
  ...product
}) => {
  const isMobileView = useMediaQuery(theme.breakpoints.only('mobile'));
  const isSlabPrice = (slabPriceTiers?.length ?? 0) > 0;
  const {priceValue, dynamicDiscount} = getSlabPriceValue(slabPriceTiers, productCount, product.displayPrice);
  const isDiscounted = product.originPrice && product.originPrice > priceValue;
  const priceUnit = 'THB';
  const kgText = showKg ? `${t('labels.kg')}` : '';
  const productPrice = showKg ? priceValue / moq : priceValue;
  const productOriginalPrice = showKg ? product.originPrice / moq : product.originPrice;
  const isReactNativeView = ReactNative.isReactNativeWebView();

  const _renderSlabPriceInfoMessage = () => {
    let infoText = '';
    if (dynamicDiscount.startCount && !dynamicDiscount.endCount) {
      infoText = `${t('wholesale.buy')} ${dynamicDiscount.startCount}${
        showKg ? `+ ${kgText}` : t('wholesale.plusUnits')
      }`;
    }
    if (dynamicDiscount.startCount && dynamicDiscount.endCount) {
      infoText = `${t('wholesale.buy')} ${dynamicDiscount.startCount}-${dynamicDiscount.endCount} ${
        showKg ? kgText : t('wholesale.units')
      }`;
    }

    return <StyledSlabPriceInfo>{infoText}</StyledSlabPriceInfo>;
  };

  const _renderPerUnitPriceInfo = () => {
    if (!productSize) {
      return <></>;
    }
    const perUnitPrice = getPricePerUnit(productSize, priceValue);
    const perUnitPriceInfo = `${formatPriceWithCurrency(perUnitPrice, priceUnit)} ${getUnitPerProduct(
      productSize,
      isWeightedFresh
    )}`;
    return <StyledUnitPerProductInfo>{perUnitPriceInfo}</StyledUnitPerProductInfo>;
  };

  return (
    <StyledDicountContainer>
      <StyledDiscountBox>
        <PriceBox className={`${product.className}`} flexDirection={'row'}>
          <PriceStyled
            data-test-id={isDiscounted ? `${productId}_discount_price` : `${productId}_original_price`}
            color={isDiscounted ? 'primary' : 'grey.900'}
            isReactNativeView={isReactNativeView || isMobileView}
          >
            {formatPrice(productPrice)}
            <UnitPrice>{getCurrencySymbol()}</UnitPrice>
            {showKg ? <FavListDiscountWeightedFreshKgText>{kgText}</FavListDiscountWeightedFreshKgText> : null}
          </PriceStyled>
          {isVoucherUsed && <VoucherIcon size={16} />}
          {isDiscounted ? (
            <PriceStriked data-test-id={`${productId}_striked_original_price`}>
              {formatPriceWithCurrency(productOriginalPrice, priceUnit)}
            </PriceStriked>
          ) : null}
        </PriceBox>
        {isInstallmentOrder && <TruckBulky width={15} height={15} />}
      </StyledDiscountBox>
      {isSlabPrice && _renderSlabPriceInfoMessage()}
      {isShowPerUnitPrice && !isSlabPrice && _renderPerUnitPriceInfo()}
    </StyledDicountContainer>
  );
};

export default DiscountPriceBox;
